var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { v4 } from 'uuid';
import { ApiError } from '../apiError';
import { CONTACTS_DELETE_MAX_AMOUNT } from '../constants';
import { debouncePromise } from '../utils';
import BaseWithPoling from './baseWithPolingApi';
export default class ContactApi extends BaseWithPoling {
    constructor(apiCall, batchProcessApi, bookServiceApi) {
        super(apiCall, batchProcessApi);
        this.apiCall = apiCall;
        this.batchProcessApi = batchProcessApi;
        this.bookServiceApi = bookServiceApi;
        this.apiBasePath = 'contact';
    }
    getContact(id, params = {}) {
        return this.apiCall({
            method: 'GET',
            route: `${this.apiBasePath}/${id}`,
            params: params,
        });
    }
    listContact(params = {}) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/list`,
            data: params,
        });
    }
    listContacts(params = {}) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/list`,
            data: params,
        });
    }
    findContacts(params = {}) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/find`,
            data: params,
        });
    }
    countContacts(params = {}) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/count`,
            data: params,
        });
    }
    findFirst(params) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/find-first`,
            data: params,
        });
    }
    updateContact(id, data) {
        return this.apiCall({
            method: 'PATCH',
            route: `${this.apiBasePath}/${id}`,
            data,
        });
    }
    deleteContact(id, data = {}) {
        return this.apiCall({
            method: 'DELETE',
            route: `${this.apiBasePath}/${id}`,
            data,
        });
    }
    deleteMulti(data) {
        if ((Array.isArray(data.ids) && data.ids.length >= CONTACTS_DELETE_MAX_AMOUNT) || data.all) {
            return this.bulkDeleteContacts(data);
        }
        return this.apiCall({
            method: 'DELETE',
            route: `${this.apiBasePath}`,
            data,
        });
    }
    deleteContactsByBook(bookId_1) {
        return __awaiter(this, arguments, void 0, function* (bookId, notifyEventProxy = false) {
            const contacts = yield this.listContact({ contact_book: bookId });
            return this.bulkDeleteContacts({
                ids: contacts.items.map(({ id }) => id),
                contact_book: bookId,
                notifyEventProxy,
            });
        });
    }
    initDeleteBookContactsBulk(bookId_1) {
        return __awaiter(this, arguments, void 0, function* (bookId, notifyEventProxy = false, limit = CONTACTS_DELETE_MAX_AMOUNT, withPoling = false) {
            const { contactsCount } = yield this.bookServiceApi.getContactBook(bookId);
            const contacts = yield this.listContact({
                contact_book: bookId,
                size: limit,
                projection: ['id'],
            });
            const id = v4();
            yield this.runSingleDeleteContactsBulk({
                ids: contacts.items.map(({ id }) => id),
                contact_book: bookId,
                notifyEventProxy,
            }, withPoling, id);
            return {
                batchId: id,
                totalBatches: Math.floor(contactsCount / limit),
                firstBatchSize: limit,
            };
        });
    }
    createContact(data) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}`,
            data,
        });
    }
    mergeContacts(id, data) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/${id}/merge`,
            data,
        });
    }
    linkContact(id, data) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/${id}/link`,
            data,
        });
    }
    linkContactsMulti(data) {
        return this.apiCall({
            method: 'POST',
            route: `${this.apiBasePath}/link`,
            data,
        });
    }
    unlinkContact(id, data) {
        return this.apiCall({
            method: 'DELETE',
            route: `${this.apiBasePath}/${id}/link`,
            data,
        });
    }
    unlinkContactsMulti(data) {
        return this.apiCall({
            method: 'DELETE',
            route: `${this.apiBasePath}/link`,
            data,
        });
    }
    getContactBooks(id) {
        return this.apiCall({
            method: 'GET',
            route: `${this.apiBasePath}/${id}/books`,
        });
    }
    isContactInBook(contactId, bookId) {
        return __awaiter(this, void 0, void 0, function* () {
            const contactBooks = yield this.getContactBooks(contactId);
            return contactBooks.includes(bookId);
        });
    }
    bulkDeleteContacts(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const { ids, contact_book, all } = data, rest = __rest(data, ["ids", "contact_book", "all"]);
            if (all) {
                if (!contact_book) {
                    throw new ApiError(400, 'contact_book should be provided in case if "all" is true');
                }
                return yield this.deleteContactsByBook(contact_book);
            }
            if (!ids) {
                throw new ApiError(400, 'Provide either "contact_book" or "all"');
            }
            const batchSize = CONTACTS_DELETE_MAX_AMOUNT;
            for (let i = 0; i < ids.length; i += batchSize) {
                const chunk = ids.slice(i, i + batchSize);
                yield this.runSingleDeleteContactsBulk(Object.assign({ ids: chunk, contact_book }, rest));
            }
        });
    }
    runSingleDeleteContactsBulk(data_1) {
        return __awaiter(this, arguments, void 0, function* (data, withPoling = true, batchId) {
            const id = batchId || v4();
            yield this.apiCall({
                method: 'DELETE',
                route: `${this.apiBasePath}/bulk`,
                data: Object.assign(Object.assign({}, data), { batchId: id }),
            });
            if (withPoling) {
                yield debouncePromise(() => this.polling(id), 3000);
            }
        });
    }
}
//# sourceMappingURL=contactApi.js.map